<script setup>
import DebugInfo from '@/components/Debug/DebugInfo.vue'
import { computed, ref, useAttrs, watch } from 'vue'
import { nanoid } from 'nanoid'
import { useI18n } from 'vue-i18n'

defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
const emit = defineEmits([ 'update:modelValue' ])
const i18n = useI18n()
const props = defineProps({
	id: { type: String, default: () => nanoid(6) },
	label: { type: String, default: null },
	options: { type: Array, default: () => [] },
	fieldValue: { type: String, default: 'id' },
	fieldLabel: { type: String, default: 'label' },
	fieldDisabled: { type: String, default: null },
	modelValue: { type: [ String, Number, Boolean ], default: null },
	hideNull: { type: Boolean, default: false },
	loading: { type: Boolean, default: false },
	noMargin: { type: Boolean, default: false },
	sortAlphabetical: { type: Boolean, default: false }
})

const localValue = ref(null)

const isRequired = computed(() => [ '', true ].includes(attrs?.required ?? false))
const sortedOptions = computed(() => {
	const sortedOptions = [ ...props.options ]
	if (props.sortAlphabetical) {
		sortedOptions.sort((a, b) => a[props.fieldLabel].localeCompare(b[props.fieldLabel]))
	}
	return sortedOptions
})

watch(() => props.modelValue, newValue => {
	localValue.value = newValue
}, { immediate: true })
watch(localValue, newValue => { emit('update:modelValue', newValue) })
</script>

<template>
	<div
		:class="{'mbe-3': !noMargin }"
		:data-testid="props.id"
		data-field="select"
		v-bind="null"
	>
		<div
			v-if="label || $slots.action"
			class="d-flex flex-wrap gap-1 mbe-1"
		>
			<label
				v-if="label"
				:for="id"
				class="mie-auto"
			>
				{{ label }}
				<font-awesome-icon
					v-if="isRequired"
					class="text-negative"
					icon="asterisk"
					size="xs"
				/>
				<DebugInfo class="badge badge-small badge-debug mis-2">FormFieldSelect</DebugInfo>
			</label>
			<slot
				v-if="$slots.action"
				class="mis-auto"
				name="action"
			/>
		</div>
		<div class="input-group input-group-fill">
			<slot
				v-if="$slots.prepend"
				name="prepend"
			/>
			<select
				:id="id"
				v-model="localValue"
				v-bind="$attrs"
			>
				<option
					v-if="!hideNull"
					:label="loading ? i18n.t('common.loading') : i18n.t('common.selectOption')"
					:value="null"
					disabled
				/>
				<option
					v-if="options.length === 0"
					:label="i18n.t('common.noResults')"
					:value="null"
					disabled
				/>
				<option
					v-if="loading"
					:value="modelValue"
					disabled
				>
					{{ i18n.t('common.loading') }}
				</option>
				<option
					v-for="option in sortedOptions"
					:key="option[fieldValue]"
					:disabled="fieldDisabled !== null && option[fieldDisabled] === true && modelValue !== option[fieldValue]"
					:value="option[fieldValue]"
				>
					{{ option[fieldLabel] }}
				</option>
			</select>
			<slot
				v-if="$slots.append"
				name="append"
			/>
		</div>
		<div
			v-if="$slots.help || $slots.info"
			class="d-flex flex-wrap gap-1 mbs-1"
		>
			<div
				v-if="$slots.help"
				class="mie-auto"
			>
				<slot name="help" />
			</div>
			<div
				v-if="$slots.info"
				class="mis-auto"
			>
				<slot name="info" />
			</div>
		</div>
	</div>
</template>
